import { useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { useCarriers } from '../lib/useCarriers.js';

interface CarrierCompleteness {
  carrierId: string;
  periods: number;
  gaps: { afterPeriod: string; beforePeriod: string; missingMonths: number }[];
  partials: { period: string; memberCount: number; baseline: number; dropPct: number }[];
  ok: boolean;
}

const DISMISS_KEY = 'cs-completeness-dismissed';

/** Stable fingerprint of the current warning set — a dismissal only holds while
 *  the warnings are UNCHANGED; new gaps/partials resurface the banner. */
function fingerprint(flagged: CarrierCompleteness[]): string {
  return flagged
    .map((f) => `${f.carrierId}:${f.gaps.length}g${f.partials.length}p`)
    .sort()
    .join('|');
}

/**
 * Warns when a carrier's period history looks incomplete — a missing month or a
 * partial (half-loaded) statement — so the attrition / book-value figures aren't
 * misread. A six-year backfill can flag dozens of carriers at once, so the
 * banner is a one-line summary by default (expandable), and DISMISSIBLE: dismissed
 * warnings collapse to a small chip the user can reopen any time, and any CHANGE
 * in the warning set brings the banner back on its own.
 */
export function CompletenessBanner({
  carrierId,
  workspaceId,
}: {
  carrierId?: string;
  workspaceId?: string;
}) {
  const { baseUrl, token } = useAuth();
  const { carriers } = useCarriers();
  const [flagged, setFlagged] = useState<CarrierCompleteness[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [dismissedFp, setDismissedFp] = useState<string | null>(() =>
    localStorage.getItem(DISMISS_KEY),
  );

  useEffect(() => {
    let active = true;
    const qs = new URLSearchParams();
    if (carrierId) qs.set('carrierId', carrierId);
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/reports/completeness?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (b) =>
          active && b && setFlagged((b.carriers as CarrierCompleteness[]).filter((x) => !x.ok)),
      )
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [baseUrl, token, carrierId, workspaceId]);

  if (flagged.length === 0) return null;
  const name = (id: string) => carriers.find((c) => c.id === id)?.name ?? id.slice(0, 8);
  const fp = fingerprint(flagged);
  const totalFindings = flagged.reduce((s, f) => s + f.gaps.length + f.partials.length, 0);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, fp);
    setDismissedFp(fp);
    setExpanded(false);
  };
  const reopen = () => {
    localStorage.removeItem(DISMISS_KEY);
    setDismissedFp(null);
  };

  // Dismissed (and unchanged since): a quiet chip that reopens the details.
  if (dismissedFp === fp) {
    return (
      <button
        type="button"
        onClick={reopen}
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-800 transition hover:bg-amber-100 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300"
        title="Show the completeness warnings"
      >
        ⚠ Completeness warnings ({flagged.length} carrier{flagged.length === 1 ? '' : 's'}) — show
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold">
          ⚠ {flagged.length} carrier{flagged.length === 1 ? '' : 's'} have periods that look
          incomplete ({totalFindings} finding{totalFindings === 1 ? '' : 's'}) — churn and
          book-value figures may be overstated.
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded-md border border-amber-400 px-2 py-0.5 text-xs font-medium text-amber-800 transition hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/40"
          >
            {expanded ? 'Hide details' : 'Show details'}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md px-2 py-0.5 text-xs text-amber-700 transition hover:bg-amber-100 dark:text-amber-300/80 dark:hover:bg-amber-900/40"
            title="Hide these warnings — they stay one click away and return automatically if anything changes"
          >
            Dismiss
          </button>
        </div>
      </div>
      {expanded && (
        <>
          <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto pr-1">
            {flagged.map((f) => {
              const parts: string[] = [];
              for (const g of f.gaps.slice(0, 2))
                parts.push(`${g.missingMonths}-month gap after ${g.afterPeriod}`);
              for (const p of f.partials.slice(0, 3))
                parts.push(`${p.period} looks partial (${p.memberCount} vs ~${p.baseline})`);
              const extra = f.gaps.length + f.partials.length - parts.length;
              return (
                <li key={f.carrierId}>
                  <span className="font-medium">{name(f.carrierId)}:</span> {parts.join(' · ')}
                  {extra > 0 ? ` · +${extra} more` : ''}
                </li>
              );
            })}
          </ul>
          <div className="mt-1.5 text-xs text-amber-700 dark:text-amber-300/80">
            A missing month or half-loaded statement makes members look "dropped." Load the missing
            statements to correct the numbers.
          </div>
        </>
      )}
    </div>
  );
}
